import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@xtnd/web/shared/layout';
import {
  Button,
  Switch,
  Label,
  Input,
  Icon,
  Card,
  CardHeader,
  CardBody,
  showToast,
  Select,
  type SelectOption,
} from '@repo/ui';
import { useTenantConfig } from '../queries/useTenantQuery';
import { useReportingConfigMutation } from '../queries/useReportingQuery';
import { useTemplates } from '../queries/useTemplateQuery';
import { tenantSettingsApi } from '../api/tenantSettingsApi';
import { clioIntegrationApi } from '../api/clioIntegrationApi';
import type { ReportingModuleConfig } from '@xtnd/web/shared/types/tenantSettingTypes';

interface ReportingFormData {
  enabled: boolean;
  dataProcessingStartDate?: string;
  arExceptionReporting: {
    enabled: boolean;
  };
  trustReconciliationReporting: {
    enabled: boolean;
    liabilityAccountId?: string;
    trustAccounts: Array<{
      clioTrustAccountId: string;
      qboTrustAccountId: string;
      trustAccountName: string;
      trustAccountDescription?: string;
    }>;
    pdfTemplateId?: string;
  };
  matterLedgerReporting: {
    enabled: boolean;
    pdfTemplateId?: string;
  };
  matterStatementReporting: {
    enabled: boolean;
    pdfTemplateId?: string;
  };
}

export default function ReportingConfigPage() {
  const navigate = useNavigate();
  const { data: tenantConfig, isLoading } = useTenantConfig();
  const reportingConfigMutation = useReportingConfigMutation();

  // Fetch available templates
  const { data: templates = [] } = useTemplates();

  // Filter to only PDF templates
  const pdfTemplates = useMemo(() => templates.filter(t => t.type === 'pdf'), [templates]);

  // Convert PDF templates to SelectOption format
  const pdfTemplateOptions: SelectOption[] = useMemo(() => {
    return pdfTemplates.map(template => ({
      value: template.id,
      label: template.name,
    }));
  }, [pdfTemplates]);

  const [formData, setFormData] = useState<ReportingFormData>({
    enabled: true,
    dataProcessingStartDate: '2024-01-01',
    arExceptionReporting: {
      enabled: true,
    },
    trustReconciliationReporting: {
      enabled: false,
      liabilityAccountId: '',
      trustAccounts: [],
      pdfTemplateId: '',
    },
    matterLedgerReporting: {
      enabled: false,
      pdfTemplateId: '',
    },
    matterStatementReporting: {
      enabled: false,
      pdfTemplateId: '',
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Account options state
  const [qboTrustAccounts, setQboTrustAccounts] = useState<Array<{
    accountId: string;
    name: string;
    accountSubType: string;
    accountType: string;
  }>>([]);

  const [qboLiabilityAccounts, setQboLiabilityAccounts] = useState<Array<{
    accountId: string;
    name: string;
    accountSubType: string;
    accountType: string;
  }>>([]);

  const [clioTrustAccounts, setClioTrustAccounts] = useState<Array<{
    id: string;
    name: string;
    account_number?: string;
  }>>([]);

  // Fetch account options when trust reconciliation is enabled
  useEffect(() => {
    if (formData.trustReconciliationReporting.enabled && formData.enabled) {
      const fetchAccounts = async () => {
        // Fetch QBO accounts
        try {
          const [qboTrustRaw, qboLiabilityRaw] = await Promise.all([
            tenantSettingsApi.getQboTrustAccounts(),
            tenantSettingsApi.getQboTrustLiabilityAccounts()
          ]);

          // QBO data is now returned in proper format from backend
          const qboTrust = qboTrustRaw;
          const qboLiability = qboLiabilityRaw;

          setQboTrustAccounts(qboTrust);
          setQboLiabilityAccounts(qboLiability);
        } catch (error) {
          console.error('Failed to fetch QBO account options:', error);
          showToast.error('Failed to load QBO account options');
        }

        // Fetch Clio accounts (independently, so QBO accounts still load if Clio fails)
        try {
          const clioTrust = await clioIntegrationApi.getTrustBankAccounts();
          setClioTrustAccounts(clioTrust);
        } catch (error) {
          console.error('Failed to fetch Clio trust accounts:', error);
          // Don't show error toast for Clio accounts - they might not be configured yet
          // Just log the error and leave the dropdown empty
        }
      };
      fetchAccounts();
    }
  }, [formData.trustReconciliationReporting.enabled, formData.enabled]);

  useEffect(() => {
    if (tenantConfig?.modules?.reporting) {
      const reporting = tenantConfig.modules.reporting as ReportingModuleConfig;
      setFormData({
        enabled: reporting.enabled ?? true,
        dataProcessingStartDate: reporting.dataProcessingStartDate ?? '2024-01-01',
        arExceptionReporting: {
          enabled: reporting.arExceptionReporting?.enabled ?? true,
        },
        trustReconciliationReporting: {
          enabled: reporting.trustReconciliationReporting?.enabled ?? false,
          liabilityAccountId: reporting.trustReconciliationReporting?.liabilityAccountId ?? '',
          trustAccounts: reporting.trustReconciliationReporting?.trustAccounts ?? [],
          pdfTemplateId: reporting.trustReconciliationReporting?.pdfTemplateId ?? '',
        },
        matterLedgerReporting: {
          enabled: reporting.matterLedgerReporting?.enabled ?? false,
          pdfTemplateId: reporting.matterLedgerReporting?.pdfTemplateId ?? '',
        },
        matterStatementReporting: {
          enabled: reporting.matterStatementReporting?.enabled ?? false,
          pdfTemplateId: reporting.matterStatementReporting?.pdfTemplateId ?? '',
        },
      });
    }
  }, [tenantConfig]);

  // Transform account data to Select options
  const qboLiabilityAccountOptions: SelectOption[] = useMemo(() => {
    return qboLiabilityAccounts.map(account => ({
      value: account.accountId,
      label: account.name
    }));
  }, [qboLiabilityAccounts]);

  const clioTrustAccountOptions: SelectOption[] = useMemo(() => {
    return clioTrustAccounts.map(account => ({
      value: account.id,
      label: account.account_number ? `${account.name} - ${account.account_number}` : account.name
    }));
  }, [clioTrustAccounts]);

  const qboTrustAccountOptions: SelectOption[] = useMemo(() => {
    return qboTrustAccounts.map(account => ({
      value: account.accountId,
      label: account.name
    }));
  }, [qboTrustAccounts]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.trustReconciliationReporting.enabled && formData.enabled) {
      if (!formData.trustReconciliationReporting.liabilityAccountId?.trim()) {
        newErrors.liabilityAccountId = 'QBO Trust Liability Account ID is required';
      }

      if (!formData.trustReconciliationReporting.trustAccounts ||
          formData.trustReconciliationReporting.trustAccounts.length === 0) {
        newErrors.trustAccounts = 'At least one trust account is required when trust reconciliation is enabled';
      } else {
        // Validate each trust account
        const usedClioAccountIds = new Set<string>();
        const usedQboAccountIds = new Set<string>();

        formData.trustReconciliationReporting.trustAccounts.forEach((account, index) => {
          if (!account.clioTrustAccountId?.trim()) {
            newErrors[`trustAccounts[${index}].clioTrustAccountId`] = `Clio Trust Account ID is required for account ${index + 1}`;
          } else if (usedClioAccountIds.has(account.clioTrustAccountId)) {
            newErrors[`trustAccounts[${index}].clioTrustAccountId`] = `Clio Trust Account is already used in another trust account pair`;
          } else {
            usedClioAccountIds.add(account.clioTrustAccountId);
          }

          if (!account.qboTrustAccountId?.trim()) {
            newErrors[`trustAccounts[${index}].qboTrustAccountId`] = `QBO Trust Account ID is required for account ${index + 1}`;
          } else if (usedQboAccountIds.has(account.qboTrustAccountId)) {
            newErrors[`trustAccounts[${index}].qboTrustAccountId`] = `QBO Trust Account is already used in another trust account pair`;
          } else {
            usedQboAccountIds.add(account.qboTrustAccountId);
          }

          if (!account.trustAccountName?.trim()) {
            newErrors[`trustAccounts[${index}].trustAccountName`] = `Trust Account Name is required for account ${index + 1}`;
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showToast.error('Please fix validation errors before saving');
      return;
    }

    try {
      await reportingConfigMutation.mutateAsync(formData);
      // Toast success message is handled in mutation hook
    } catch (error) {
      // Error toast is handled in mutation hook
      console.error('Failed to save reporting configuration:', error);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  return (
    <PageLayout
      title="Reporting Configuration"
      description="Configure reporting features and account settings"
      icon="HiDocumentText"
      isLoading={isLoading}
    >
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSave();
        }}
        className="space-y-8"
      >
        {/* Master Enable Toggle */}
        <div className="bg-white dark:bg-xtnd-dark-800 rounded-lg border border-xtnd-dark-100 dark:border-xtnd-dark-600 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-xtnd-blue/10 dark:bg-xtnd-blue/20 rounded-lg">
                <Icon name="HiDocumentText" className="h-5 w-5 text-xtnd-blue" />
              </div>
              <div>
                <Label
                  htmlFor="reporting-enabled"
                  className="text-base font-semibold text-xtnd-dark dark:text-xtnd-white"
                >
                  Enable Reporting Module
                </Label>
                <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
                  Master switch for all reporting features
                </p>
              </div>
            </div>
            <Switch
              id="reporting-enabled"
              checked={formData.enabled}
              onChange={checked => setFormData({ ...formData, enabled: checked })}
              disabled={isLoading || reportingConfigMutation.isPending}
            />
          </div>
        </div>

        {/* Data Processing Start Date Section */}
        {formData.enabled && (
          <div className="bg-white dark:bg-xtnd-dark-800 rounded-lg border border-xtnd-dark-100 dark:border-xtnd-dark-600 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Icon name="HiCalendar" className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <Label
                  htmlFor="data-processing-start-date"
                  className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white"
                >
                  Data Processing Start Date
                </Label>
                <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
                  The earliest date for processing historical data in backfill operations
                </p>
              </div>
            </div>
            <div className="max-w-xs">
              <Input
                id="data-processing-start-date"
                type="date"
                value={formData.dataProcessingStartDate || '2024-01-01'}
                onChange={(e) => setFormData({
                  ...formData,
                  dataProcessingStartDate: e.target.value,
                })}
                disabled={isLoading || reportingConfigMutation.isPending}
                label="Start Date"
                helperText="Format: YYYY-MM-DD (e.g., 2024-01-01)"
              />
            </div>
          </div>
        )}

        {/* Reporting Features Grid */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${
            !formData.enabled ? 'opacity-50 pointer-events-none' : ''
          }`}
        >
          {/* AR Exception Reporting Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <Icon name="HiInformationCircle" className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-xtnd-Sdark dark:text-xtnd-white">
                    AR Exception Reporting
                  </h3>
                  <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                    Accounts receivable exception reporting
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex items-center justify-between py-3 px-4 bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg">
                <div className="flex-1">
                  <Label
                    htmlFor="ar-exception-enabled"
                    className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                  >
                    Enable AR Exception Reporting
                  </Label>
                  <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
                    Track and report accounts receivable exceptions
                  </p>
                </div>
                <Switch
                  id="ar-exception-enabled"
                  checked={formData.arExceptionReporting.enabled}
                  onChange={checked =>
                    setFormData({
                      ...formData,
                      arExceptionReporting: {
                        ...formData.arExceptionReporting,
                        enabled: checked,
                      },
                    })
                  }
                  disabled={!formData.enabled || isLoading || reportingConfigMutation.isPending}
                />
              </div>
            </CardBody>
          </Card>

          {/* Matter Ledger Report Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-xtnd-blue/10 dark:bg-xtnd-blue/20 rounded-lg">
                  <Icon name="HiTable" className="h-5 w-5 text-xtnd-blue" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                    Matter Ledger Report
                  </h3>
                  <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                    Detailed transaction history for matters
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex items-center justify-between py-3 px-4 bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg">
                <div className="flex-1">
                  <Label
                    htmlFor="matter-ledger-enabled"
                    className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                  >
                    Enable Matter Ledger Report
                  </Label>
                  <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
                    View detailed transaction history for specific matters
                  </p>
                </div>
                <Switch
                  id="matter-ledger-enabled"
                  checked={formData.matterLedgerReporting.enabled}
                  onChange={checked =>
                    setFormData({
                      ...formData,
                      matterLedgerReporting: {
                        ...formData.matterLedgerReporting,
                        enabled: checked,
                      },
                    })
                  }
                  disabled={!formData.enabled || isLoading || reportingConfigMutation.isPending}
                />
              </div>

              {/* PDF Template Selection */}
              {formData.matterLedgerReporting.enabled && formData.enabled && (
                <div className="mt-4 pt-4 border-t border-xtnd-dark-100 dark:border-xtnd-dark-600">
                  <Select<SelectOption>
                    label="PDF Template"
                    value={
                      formData.matterLedgerReporting.pdfTemplateId
                        ? pdfTemplateOptions.find(
                            opt => opt.value === formData.matterLedgerReporting.pdfTemplateId
                          ) || null
                        : null
                    }
                    onChange={option => {
                      setFormData({
                        ...formData,
                        matterLedgerReporting: {
                          ...formData.matterLedgerReporting,
                          pdfTemplateId: option?.value || '',
                        },
                      });
                    }}
                    options={pdfTemplateOptions}
                    placeholder="Select PDF template..."
                    helperText="Select PDF template for Matter Ledger Report exports"
                    disabled={isLoading || reportingConfigMutation.isPending}
                    isLoading={isLoading || reportingConfigMutation.isPending}
                    fullWidth={true}
                  />
                </div>
              )}
            </CardBody>
          </Card>

          {/* Matter Statement Report Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-xtnd-blue/10 dark:bg-xtnd-blue/20 rounded-lg">
                  <Icon name="HiDocumentText" className="h-5 w-5 text-xtnd-blue" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                    Matter Statement Report
                  </h3>
                  <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                    Comprehensive account statements for matters
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex items-center justify-between py-3 px-4 bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg">
                <div className="flex-1">
                  <Label
                    htmlFor="matter-statement-enabled"
                    className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                  >
                    Enable Matter Statement Report
                  </Label>
                  <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
                    Generate comprehensive account statements for specific matters
                  </p>
                </div>
                <Switch
                  id="matter-statement-enabled"
                  checked={formData.matterStatementReporting.enabled}
                  onChange={checked =>
                    setFormData({
                      ...formData,
                      matterStatementReporting: {
                        ...formData.matterStatementReporting,
                        enabled: checked,
                      },
                    })
                  }
                  disabled={!formData.enabled || isLoading || reportingConfigMutation.isPending}
                />
              </div>

              {/* PDF Template Selection */}
              {formData.matterStatementReporting.enabled && formData.enabled && (
                <div className="mt-4 pt-4 border-t border-xtnd-dark-100 dark:border-xtnd-dark-600">
                  <Select<SelectOption>
                    label="PDF Template"
                    value={
                      formData.matterStatementReporting.pdfTemplateId
                        ? pdfTemplateOptions.find(
                            opt => opt.value === formData.matterStatementReporting.pdfTemplateId
                          ) || null
                        : null
                    }
                    onChange={option => {
                      setFormData({
                        ...formData,
                        matterStatementReporting: {
                          ...formData.matterStatementReporting,
                          pdfTemplateId: option?.value || '',
                        },
                      });
                    }}
                    options={pdfTemplateOptions}
                    placeholder="Select PDF template..."
                    helperText="Select PDF template for Matter Statement Report exports"
                    disabled={isLoading || reportingConfigMutation.isPending}
                    isLoading={isLoading || reportingConfigMutation.isPending}
                    fullWidth={true}
                  />
                </div>
              )}
            </CardBody>
          </Card>

          {/* Trust Reconciliation Reporting Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-xtnd-blue/10 dark:bg-xtnd-blue/20 rounded-lg">
                  <Icon name="HiShieldCheck" className="h-5 w-5 text-xtnd-blue" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                    3-Way Trust Reconciliation
                  </h3>
                  <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                    Trust account reconciliation reporting
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="flex items-center justify-between py-3 px-4 bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg">
                <div className="flex-1">
                  <Label
                    htmlFor="trust-reconciliation-enabled"
                    className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                  >
                    Enable Trust Reconciliation
                  </Label>
                  <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
                    Verify Clio Trust transactions match QBO accounts
                  </p>
                </div>
                <Switch
                  id="trust-reconciliation-enabled"
                  checked={formData.trustReconciliationReporting.enabled}
                  onChange={checked =>
                    setFormData({
                      ...formData,
                      trustReconciliationReporting: {
                        ...formData.trustReconciliationReporting,
                        enabled: checked,
                      },
                    })
                  }
                  disabled={!formData.enabled || isLoading || reportingConfigMutation.isPending}
                />
              </div>

                  {/* Account Configuration */}
                  {formData.trustReconciliationReporting.enabled && formData.enabled && (
                    <div className="space-y-4 mt-4 pt-4 border-t border-xtnd-dark-100 dark:border-xtnd-dark-600">
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-white mb-1">
                          Trust Account Configuration
                        </h4>
                        <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                          Configure the liability account and multiple trust account pairs for reconciliation
                        </p>
                      </div>

                      {/* Liability Account Configuration */}
                      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <h5 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-3">
                          Liability Account (Global)
                        </h5>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                          This QBO liability account applies to all trust account reconciliations
                        </p>
                        <Select
                          label="QBO Trust Liability Account"
                          value={qboLiabilityAccountOptions.find(option =>
                            option.value === formData.trustReconciliationReporting.liabilityAccountId
                          ) || null}
                          onChange={(option) => {
                            setFormData({
                              ...formData,
                              trustReconciliationReporting: {
                                ...formData.trustReconciliationReporting,
                                liabilityAccountId: option?.value || '',
                              },
                            });
                            if (errors.liabilityAccountId) {
                              setErrors({ ...errors, liabilityAccountId: undefined });
                            }
                          }}
                          options={qboLiabilityAccountOptions}
                          error={errors.liabilityAccountId}
                          required
                          disabled={isLoading || reportingConfigMutation.isPending}
                          loading={qboLiabilityAccounts.length === 0}
                          placeholder="Select liability account..."
                          fullWidth
                        />
                      </div>

                      {/* Trust Accounts Configuration */}
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h5 className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white">
                            Trust Account Pairs
                          </h5>
                          <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                            Configure multiple Clio/QBO trust account pairs
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Check if we have available accounts that haven't been used yet
                            const usedClioAccountIds = new Set(
                              formData.trustReconciliationReporting.trustAccounts
                                .map(account => account.clioTrustAccountId)
                                .filter(id => id?.trim())
                            );
                            const usedQboAccountIds = new Set(
                              formData.trustReconciliationReporting.trustAccounts
                                .map(account => account.qboTrustAccountId)
                                .filter(id => id?.trim())
                            );

                            const availableClioAccounts = clioTrustAccounts.filter(account => !usedClioAccountIds.has(account.id));
                            const availableQboAccounts = qboTrustAccounts.filter(account => !usedQboAccountIds.has(account.accountId));

                            if (availableClioAccounts.length === 0 || availableQboAccounts.length === 0) {
                              showToast.error('All available trust accounts have already been paired. Remove an existing pair before adding another.');
                              return;
                            }

                            const newAccount = {
                              clioTrustAccountId: '',
                              qboTrustAccountId: '',
                              trustAccountName: `Trust Account ${formData.trustReconciliationReporting.trustAccounts.length + 1}`,
                              trustAccountDescription: ''
                            };
                            setFormData({
                              ...formData,
                              trustReconciliationReporting: {
                                ...formData.trustReconciliationReporting,
                                trustAccounts: [...formData.trustReconciliationReporting.trustAccounts, newAccount]
                              }
                            });
                          }}
                          disabled={isLoading || reportingConfigMutation.isPending}
                          leftIcon={<Icon name="HiPlus" />}
                        >
                          Add Account
                        </Button>
                      </div>

                      {formData.trustReconciliationReporting.trustAccounts.length === 0 ? (
                        <div className="text-center py-8 text-xtnd-dark-400 dark:text-xtnd-dark-300">
                          <Icon name="HiCreditCard" className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p className="text-sm">No trust accounts configured</p>
                          <p className="text-xs">Click "Add Account" to create your first trust account pair</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {formData.trustReconciliationReporting.trustAccounts.map((account, index) => (
                            <Card key={index} variant="outlined" className="p-4">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white">
                                    {account.trustAccountName || `Trust Account ${index + 1}`}
                                  </h5>
                                  {account.trustAccountDescription && (
                                    <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
                                      {account.trustAccountDescription}
                                    </p>
                                  )}
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updatedAccounts = formData.trustReconciliationReporting.trustAccounts.filter((_, i) => i !== index);
                                    setFormData({
                                      ...formData,
                                      trustReconciliationReporting: {
                                        ...formData.trustReconciliationReporting,
                                        trustAccounts: updatedAccounts
                                      }
                                    });
                                  }}
                                  disabled={isLoading || reportingConfigMutation.isPending}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                >
                                  <Icon name="HiTrash" className="h-4 w-4" />
                                </Button>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                  label="Trust Account Name"
                                  value={account.trustAccountName}
                                  onChange={e => {
                                    const updatedAccounts = [...formData.trustReconciliationReporting.trustAccounts];
                                    updatedAccounts[index] = { ...account, trustAccountName: e.target.value };
                                    setFormData({
                                      ...formData,
                                      trustReconciliationReporting: {
                                        ...formData.trustReconciliationReporting,
                                        trustAccounts: updatedAccounts
                                      }
                                    });
                                  }}
                                  required
                                  disabled={isLoading || reportingConfigMutation.isPending}
                                  fullWidth
                                />

                                <Input
                                  label="Description (Optional)"
                                  value={account.trustAccountDescription}
                                  onChange={e => {
                                    const updatedAccounts = [...formData.trustReconciliationReporting.trustAccounts];
                                    updatedAccounts[index] = { ...account, trustAccountDescription: e.target.value };
                                    setFormData({
                                      ...formData,
                                      trustReconciliationReporting: {
                                        ...formData.trustReconciliationReporting,
                                        trustAccounts: updatedAccounts
                                      }
                                    });
                                  }}
                                  disabled={isLoading || reportingConfigMutation.isPending}
                                  fullWidth
                                />

                                <Select
                                  label="Clio Trust Account"
                                  value={clioTrustAccountOptions.find(option =>
                                    option.value === account.clioTrustAccountId
                                  ) || null}
                                  onChange={(option) => {
                                    const updatedAccounts = [...formData.trustReconciliationReporting.trustAccounts];
                                    updatedAccounts[index] = { ...account, clioTrustAccountId: option?.value || '' };
                                    setFormData({
                                      ...formData,
                                      trustReconciliationReporting: {
                                        ...formData.trustReconciliationReporting,
                                        trustAccounts: updatedAccounts
                                      }
                                    });
                                    // Clear any errors for this field
                                    const fieldError = `trustAccounts[${index}].clioTrustAccountId`;
                                    if (errors[fieldError]) {
                                      const newErrors = { ...errors };
                                      delete newErrors[fieldError];
                                      setErrors(newErrors);
                                    }
                                  }}
                                  options={clioTrustAccountOptions}
                                  error={errors[`trustAccounts[${index}].clioTrustAccountId`]}
                                  required
                                  disabled={isLoading || reportingConfigMutation.isPending}
                                  loading={clioTrustAccounts.length === 0}
                                  placeholder="Select Clio trust account..."
                                  fullWidth
                                />

                                <Select
                                  label="QBO Trust Account"
                                  value={qboTrustAccountOptions.find(option =>
                                    option.value === account.qboTrustAccountId
                                  ) || null}
                                  onChange={(option) => {
                                    const updatedAccounts = [...formData.trustReconciliationReporting.trustAccounts];
                                    updatedAccounts[index] = { ...account, qboTrustAccountId: option?.value || '' };
                                    setFormData({
                                      ...formData,
                                      trustReconciliationReporting: {
                                        ...formData.trustReconciliationReporting,
                                        trustAccounts: updatedAccounts
                                      }
                                    });
                                    // Clear any errors for this field
                                    const fieldError = `trustAccounts[${index}].qboTrustAccountId`;
                                    if (errors[fieldError]) {
                                      const newErrors = { ...errors };
                                      delete newErrors[fieldError];
                                      setErrors(newErrors);
                                    }
                                  }}
                                  options={qboTrustAccountOptions}
                                  error={errors[`trustAccounts[${index}].qboTrustAccountId`]}
                                  required                                  
                                  disabled={isLoading || reportingConfigMutation.isPending}
                                  loading={qboTrustAccounts.length === 0}
                                  placeholder="Select QBO trust account..."
                                  fullWidth
                                />
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}

                      {/* PDF Template Selection */}
                      <div className="mt-6 pt-4 border-t border-xtnd-dark-100 dark:border-xtnd-dark-600">
                        <Select<SelectOption>
                          label="PDF Template"
                          value={
                            formData.trustReconciliationReporting.pdfTemplateId
                              ? pdfTemplateOptions.find(
                                  opt => opt.value === formData.trustReconciliationReporting.pdfTemplateId
                                ) || null
                              : null
                          }
                          onChange={option => {
                            setFormData({
                              ...formData,
                              trustReconciliationReporting: {
                                ...formData.trustReconciliationReporting,
                                pdfTemplateId: option?.value || '',
                              },
                            });
                          }}
                          options={pdfTemplateOptions}
                          placeholder="Select PDF template..."
                          disabled={!formData.enabled || isLoading || reportingConfigMutation.isPending}
                          fullWidth
                        />
                      </div>
                    </div>
                  )}
            </CardBody>
          </Card>
        </div>

        {/* Form Footer with Save Button */}
        <div className="sticky bottom-0 bg-white dark:bg-xtnd-dark-800 border-t border-xtnd-dark-100 dark:border-xtnd-dark-600 -mx-8 px-8 py-4 mt-8">
          <div className="flex items-center justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={reportingConfigMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={reportingConfigMutation.isPending}
              disabled={isLoading || Object.keys(errors).length > 0}
            >
              Save All Settings
            </Button>
          </div>
        </div>
      </form>
    </PageLayout>
  );
}
